import React from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import {
  ConditionalRenderer,
  Header,
  useLanguageTranslator,
  CoverImage,
  StatusTag,
} from "../../../components";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Skeleton from "@mui/material/Skeleton";

import { Practice } from "../../../@lib/types";
import { useParams } from "react-router-dom";
import { useQuery, gql } from "@apollo/client";
import imagePlaceholder from "../../../@lib/images/image-placeholder.svg";
import { baseURL } from "../../../utils/constants";
import { Button, Chip } from "@mui/material";
import { PDFDocIcon } from "../../../@lib/icons";
import { useApolloQuery } from "../../../hooks";

const practiceQueryDocument = gql`
  query practiceQuery($practiceId: ID!) {
    practice(id: $practiceId) {
      title
      description
      documentTitle
      country {
        name
      }
      practiceCategory {
        name
      }
      theme {
        title
      }
      practiceType {
        name
      }
      policies {
        title
      }
      people {
        title
        firstname
        lastname
      }
      rejectionReason
      picture {
        source
        originalFilename
      }
      document {
        source
        originalFilename
      }
      link
      date
      status
    }
  }
`;

const PracticeDetails: React.FC = () => {
  const params = useParams();
  const { translator } = useLanguageTranslator();

  const { data, loading } = useApolloQuery({
    queryDocument: practiceQueryDocument,
    variables: { practiceId: params.practiceId },
  });

  return (
    <div>
      <Header title={translator("Practices")} subtitle="" />

      <Container sx={{ my: 3 }}>
        <Stack direction={{ lg: "row" }} spacing={3}>
          <Paper component={Container} sx={{ py: 3, flex: 3 }}>
            <CoverImage
              image={
                data?.practice?.picture?.source
                  ? `${baseURL}${data?.practice?.picture?.source}`
                  : imagePlaceholder
              }
              height={400}
              mb={2}
            />

            <Box mt={3}>
              {loading ? (
                <>
                  <Skeleton
                    variant="text"
                    height={32}
                    width={200}
                    sx={{ mb: 0.5 }}
                  />
                  <Skeleton variant="text" height={16} width="80%" />
                  <Skeleton variant="text" height={16} width="40%" />
                </>
              ) : (
                <>
                  <StatusTag
                    label={translator(
                      `status.${
                        data?.practice?.status?.charAt(0)?.toUpperCase() +
                        data?.practice?.status?.slice(1)?.toLowerCase()
                      }`
                    )}
                    status={data?.practice?.status}
                  />
                  <ConditionalRenderer condition={data?.practice?.rejectionReason}>
                    <Typography variant="h5" fontWeight={300} mt={1}>
                      {translator(`Rejection Reason`)}:{" "}
                      {data?.practice?.rejectionReason}
                    </Typography>
                  </ConditionalRenderer>
                  <Typography variant="h4" gutterBottom>
                    {data?.practice.title}
                  </Typography>
                  <Typography variant="body2">
                    {data?.practice.description}
                  </Typography>
                </>
              )}
            </Box>

            <Grid container spacing={1} my={2}>
              <Grid item xs={12} md={6} lg={3}>
                <Typography component="p" variant="caption" gutterBottom>
                  {translator("Policies")}
                </Typography>
                <ConditionalRenderer
                  condition={data?.practice?.policies?.length <= 0}
                >
                  {translator("None")}
                </ConditionalRenderer>
                <ConditionalRenderer
                  condition={data?.practice?.policies?.length > 0}
                >
                  <Stack height={"14dvh"} sx={{ overflowY: "auto" }}>
                    {data?.practice?.policies?.map(
                      (elem: { title: string }) => (
                        <Chip
                          title={elem?.title}
                          label={elem?.title?.slice(0, 30)}
                          sx={{
                            my: 1,
                            border: "1px solid #00AAB0",
                            backgroundColor: "#eff1f7",
                            color: "#00AAB0",
                          }}
                        />
                      )
                    )}
                  </Stack>
                </ConditionalRenderer>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Typography component="p" variant="caption" gutterBottom>
                  {translator("People")}
                </Typography>
                <ConditionalRenderer
                  condition={data?.practice?.people?.length <= 0}
                >
                  {translator("None")}
                </ConditionalRenderer>
                <ConditionalRenderer
                  condition={data?.practice?.people?.length > 0}
                >
                  <Stack height={"14dvh"} sx={{ overflowY: "auto" }}>
                    {data?.practice?.people?.map(
                      (elem: { firstname: string; lastname: string }) => (
                        <Chip
                          title={`${elem?.firstname} ${elem?.lastname}`}
                          label={`${elem?.firstname} ${elem?.lastname}`}
                          sx={{
                            my: 1,
                            border: "1px solid #00AAB0",
                            backgroundColor: "#eff1f7",
                            color: "#00AAB0",
                          }}
                        />
                      )
                    )}
                  </Stack>
                </ConditionalRenderer>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Typography component="p" variant="caption" gutterBottom>
                  {translator("Country")}
                </Typography>
                <Typography mb={2}>{data?.practice.country.name}</Typography>

                <Typography component="p" variant="caption" gutterBottom>
                  {translator("generic.Date Voted")}
                </Typography>
                <Typography mb={2}>{data?.practice.date || "--"}</Typography>

                <Typography component="p" variant="caption" gutterBottom>
                  Type
                </Typography>
                <Typography mb={2}>
                  {data?.practice.practiceType.name}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Typography component="p" variant="caption" gutterBottom>
                  {translator("Theme")}
                </Typography>
                <Typography mb={2}>{data?.practice.theme.title}</Typography>

                <Typography component="p" variant="caption" gutterBottom>
                  {translator("dashboardTableHead.Category")}
                </Typography>
                <Typography mb={2}>
                  {data?.practice.practiceCategory.name}
                </Typography>

                <Typography component="p" variant="caption" gutterBottom>
                  External Practice Link
                </Typography>
                <Typography mb={2}>
                  {data?.practice.link ? (
                    <Link
                      href={data.practice.link}
                      title={data.practice.link}
                      target="_BLANK"
                      noWrap
                      display="block"
                      color="secondary"
                    >
                      {data.practice.link?.slice(0, 22)}...
                    </Link>
                  ) : (
                    "--"
                  )}
                </Typography>
              </Grid>
            </Grid>
            <ConditionalRenderer condition={!data?.practice?.document?.source}>
              <Typography variant="h3" textAlign={"center"}>
                {translator("No Document")}
              </Typography>
            </ConditionalRenderer>
            <ConditionalRenderer condition={data?.practice?.document?.source}>
              <Stack
                sx={{
                  my: 2,
                  p: 1,
                  border: "1px solid #F1F1F1",
                  borderRadius: "10px",
                }}
                direction={{ lg: "row" }}
                alignItems={{ lg: "center" }}
                justifyContent={{ lg: "space-between" }}
              >
                <div style={{ display: "flex" }}>
                  <Stack
                    sx={{
                      backgroundColor: "#fff2f2",
                      width: "90px",
                      height: "82px",
                      position: "relative",
                      zIndex: "0",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ position: "absolute", zIndex: "1000" }}>
                      <PDFDocIcon />
                    </div>
                  </Stack>
                  <Stack direction={{ lg: "column" }} sx={{ mx: 1 }}>
                    <Typography
                      variant="h4"
                      fontWeight={600}
                      color={"#494E54"}
                      fontSize={"15px"}
                      mt={2}
                      gutterBottom
                      title={data?.practice?.document?.originalFilename}
                    >
                      {data?.practice?.document?.originalFilename?.length > 40
                        ? `${data?.practice?.document?.originalFilename?.slice(
                            0,
                            40
                          )}...`
                        : data?.practice?.document?.originalFilename}
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight={600}
                      color={"#494E54"}
                      fontSize={"15px"}
                      gutterBottom
                      title={data?.practice?.documentTitle}
                    >
                      {data?.practice?.documentTitle?.length > 40
                        ? `${data?.practice?.documentTitle?.slice(0, 40)}...`
                        : data?.practice?.documentTitle}
                    </Typography>
                  </Stack>
                </div>
                <Stack
                  direction={{ lg: "column" }}
                  justifyContent={{ lg: "space-between" }}
                >
                  <Link
                    href={`${baseURL}${data?.practice.document?.source}`}
                    target="_blank"
                  >
                    <Button
                      sx={{
                        px: 5,
                        backgroundColor: "#F6A000",
                        color: "#ffffff",
                        "&:hover": {
                          backgroundColor: "primary.light",
                        },
                        width: "100%",
                      }}
                    >
                      {translator("dashboardTableHead.View")}
                    </Button>
                  </Link>
                  <Button
                    sx={{
                      px: 6,
                      mt: 1,
                      backgroundColor: "#00AAB0",
                      color: "#ffffff",
                      "&:hover": {
                        backgroundColor: "primary.light",
                      },
                    }}
                    onClick={() => {
                      const fileUrl = `${baseURL}${data?.practice.document?.source}`;
                      fetch(fileUrl)
                        .then((response) => response.blob())
                        .then((blob) => {
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement("a");
                          link.href = url;
                          link.download =
                            data?.practice.document?.originalFilename ||
                            "Document.pdf";
                          link.click();
                          window.URL.revokeObjectURL(url);
                        })
                        .catch((err) =>
                          console.error("Error downloading the file", err)
                        );
                    }}
                  >
                    {translator("Download")}
                  </Button>
                </Stack>
              </Stack>
            </ConditionalRenderer>
          </Paper>

          <Paper component={Container} sx={{ py: 3, flex: 1 }}>
            <Typography variant="h4" gutterBottom>
              Related practices
            </Typography>
          </Paper>
        </Stack>
      </Container>
    </div>
  );
};

export { PracticeDetails };
